/**
 * Books & Records Adequacy — panel.
 *
 * Reads the live assessment + items from `booksAndRecords.publicQueries`
 * (not from a task output) and renders: the graded verdict + inadequacy-date
 * card, a checklist (statutory baseline vs cross-reference gaps) as tables,
 * an "outstanding records to request" action, and a per-item drill-in.
 */

import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Card, CardContent } from "@/ui/primitives/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/ui/composed/overlays/tooltip";
import {
  RotateCcw,
  ClipboardCopy,
  BookCheck,
  ChevronRight,
  Bot,
  Info,
  TriangleAlert,
  LayoutGrid,
  Scale,
  ChevronDown,
  Ban,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

import { UnifiedFeed } from "../summarization/shared-panels";
import { DocumentDetail } from "../documents/DocumentDetail";
import { VerdictCard } from "./VerdictCard";
import { CompletenessCard } from "./CompletenessCard";
import { BooksAndRecordsItemPanel } from "./BooksAndRecordsItemPanel";
import { StateChip, SeverityChip, AdequacyChip } from "./chips";
import { STATE_META } from "./meta";
import type {
  AdequacyData,
  BooksRecordsItem,
  ItemState,
  Severity,
} from "./types";
import {
  isBankruptcyAppointmentType,
  isPersonalInsolvencyAppointmentType,
} from "@cvx/lib/appointmentTypes";

/** Filter applied by the count pills. */
type FilterKey = ItemState | "critical";

function matchesFilter(i: BooksRecordsItem, filter: FilterKey | null): boolean {
  if (!filter) return true;
  if (filter === "critical")
    return i.severity === "critical" && i.state === "outstanding";
  return i.state === filter;
}

const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function sortItems(items: BooksRecordsItem[]): BooksRecordsItem[] {
  return [...items].sort((a, b) => {
    const outA = a.state === "outstanding" ? 0 : 1;
    const outB = b.state === "outstanding" ? 0 : 1;
    if (outA !== outB) return outA - outB;
    return SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
  });
}

export function BooksAndRecordsView({
  jobId,
  isViewer,
}: {
  jobId: Id<"jobs">;
  isViewer?: boolean;
}) {
  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.booksAndRecords.publicQueries
        .getAdequacyForJob,
      { jobId },
    ),
  );

  const rerun = useMutation({
    mutationFn: useConvexAction(
      api.jobs.summarization.actions.rerunBooksAndRecords,
    ),
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [docId, setDocId] = useState<Id<"documents"> | null>(null);
  const [filter, setFilter] = useState<FilterKey | null>(null);

  const typed = data as AdequacyData | undefined;
  const items = useMemo(() => typed?.items ?? [], [typed]);
  const assessment = typed?.assessment ?? null;
  const status = typed?.task?.status ?? "waiting";
  const running = status === "running";
  const taskId = typed?.task?._id;

  // Regime-aware presentation (MOR-1254): on a personal matter the
  // Corporations Act adequacy verdict and the s286 statutory baseline don't
  // apply, but the case-specific cross-reference gaps stay useful. A Part X
  // debtor is never made bankrupt (MOR-1838), so the copy names the matter
  // and the individual accordingly.
  const isPersonal = isPersonalInsolvencyAppointmentType(
    typed?.appointmentType,
  );
  const isBankruptcy = isBankruptcyAppointmentType(typed?.appointmentType);
  const matterNoun = isBankruptcy
    ? "an individual bankruptcy"
    : "a Part X personal insolvency agreement";
  const personNoun = isBankruptcy
    ? "an individual bankrupt"
    : "an individual debtor";

  // Full task detail (activity log + working memory) for the Agent feed tab.
  // `enabled: false` still opens a Convex subscription, so idle until we
  // have a real id rather than sending "".
  const { data: taskDetail } = useQuery(
    convexQueryOrIdle(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      taskId ? { taskId } : undefined,
    ),
  );

  const { rubric, gaps, outstanding } = useMemo(() => {
    return {
      rubric: sortItems(items.filter((i) => i.itemType === "rubric")),
      gaps: sortItems(items.filter((i) => i.itemType === "dependency_gap")),
      // On a personal job the statutory-baseline rubric is not applicable, so
      // "outstanding to request" is scoped to the cross-reference gaps only.
      outstanding: sortItems(
        items.filter(
          (i) =>
            i.state === "outstanding" &&
            (!isPersonal || i.itemType === "dependency_gap"),
        ),
      ),
    };
  }, [items, isPersonal]);

  const selected = items.find((i) => i._id === selectedId) ?? null;

  const handleRerun = async () => {
    try {
      await rerun.mutateAsync({ jobId });
      toast.success("Re-assessing books & records");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to re-run");
    }
  };

  const copyOutstanding = async () => {
    const lines = outstanding.map(
      (i) =>
        `- [${i.severity.toUpperCase()}] ${i.title}${i.gapRationale ? ` — ${i.gapRationale}` : ""}`,
    );
    const text = `Books & records — outstanding documents to request:\n\n${lines.join("\n")}`;
    await navigator.clipboard.writeText(text);
    toast.success(`Copied ${outstanding.length} outstanding items`);
  };

  if (!typed) return null;

  const empty = !assessment && items.length === 0;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-1 flex-col gap-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <h1 className="text-lg font-semibold">
              Adequacy of Books &amp; Records
            </h1>
            <p className="text-xs text-muted-foreground">
              {isPersonal
                ? `Cross-reference gaps only — s286 adequacy and the s588E presumption are Corporations Act tests that do not apply to ${matterNoun}`
                : "s286 record-keeping; s588(4) presumed insolvency (inadequate records)"}
            </p>
          </div>
          {outstanding.length > 0 && (
            <Button variant="outline" size="sm" onClick={copyOutstanding}>
              <ClipboardCopy className="mr-1.5 size-3.5" />
              Copy Outstanding ({outstanding.length})
            </Button>
          )}
          {!isViewer && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRerun}
              disabled={rerun.isPending || status === "running"}
            >
              <RotateCcw className="mr-1.5 size-3.5" />
              Re-Assess
            </Button>
          )}
        </div>

        <Tabs
          defaultValue={running && !assessment ? "feed" : "adequacy"}
          className="flex flex-1 flex-col"
        >
          <TabsList className="self-start">
            <TabsTrigger value="adequacy">Adequacy</TabsTrigger>
            <TabsTrigger value="feed" className="gap-1.5">
              <Bot className="size-3.5" />
              Agent
              {running && (
                <span className="size-1.5 animate-pulse rounded-full bg-blue-500" />
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="adequacy" className="mt-4 flex flex-col gap-5">
            {isPersonal ? (
              <>
                <NotApplicableNotice
                  title={`Adequacy verdict — not applicable to ${matterNoun}`}
                >
                  The overall adequacy grading and the{" "}
                  <span className="font-medium">s588E(4)</span> presumed-
                  insolvency date are Corporations Act tests keyed to a
                  company's <span className="font-medium">s286</span> record-
                  keeping duty. They do not apply to {personNoun}. The
                  case-specific cross-reference gaps below still flag documents
                  worth requesting.
                </NotApplicableNotice>

                <div>
                  <HeadingWithTooltip
                    title="Statutory Baseline (s286)"
                    tooltip={`Section 286 of the Corporations Act 2001 (Cth) is a company record-keeping duty. It has no equivalent for ${personNoun}, so this baseline is not assessed on a personal job.`}
                  />
                  <NotApplicableNotice
                    title={`Not applicable to ${matterNoun}`}
                  >
                    The <span className="font-medium">s286</span> statutory
                    record-keeping baseline is a duty owed by companies. An
                    individual {isBankruptcy ? "bankrupt" : "debtor"} is not
                    subject to it, so the company record checklist is not
                    assessed here.
                  </NotApplicableNotice>
                </div>

                <ItemTable
                  title="Cross-Reference Gaps"
                  tooltip="Case-specific records implied by what the other agents found — e.g. a registered security interest with no underlying contract, a loan with no agreement, or employees with no payroll/STP records. These are documents you'd expect to exist given the affairs on file but which aren't in Morgan."
                  items={gaps}
                  onSelect={setSelectedId}
                  showSubtext={false}
                />
              </>
            ) : empty ? (
              <Card>
                <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                  <BookCheck className="size-8 text-muted-foreground" />
                  <div>
                    <p className="font-medium">No assessment yet</p>
                    <p className="max-w-md text-sm text-muted-foreground">
                      This agent will automatically assess the adequacy of books
                      and records as data and findings update. Re-run the
                      assessment manually by clicking the Run Assessment button
                      below.
                    </p>
                  </div>
                  {!isViewer && (
                    <Button onClick={handleRerun} disabled={rerun.isPending}>
                      <RotateCcw className="mr-2 size-4" />
                      Run Assessment
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                {assessment && (
                  <CompletenessCard
                    jobId={jobId}
                    assessment={assessment}
                    isViewer={isViewer}
                  />
                )}

                {assessment && (
                  <VerdictCard
                    jobId={jobId}
                    assessment={assessment}
                    isViewer={isViewer}
                  />
                )}

                {assessment && (
                  <FilterBar
                    counts={assessment.counts}
                    total={items.length}
                    active={filter}
                    onChange={setFilter}
                  />
                )}

                <ItemTable
                  title="Statutory Baseline (s286)"
                  tooltip="Section 286 of the Corporations Act 2001 (Cth) requires a company to keep written financial records that correctly record and explain its transactions, financial position and performance, and that would enable true and fair financial statements to be prepared and audited. Records must be retained for 7 years."
                  items={rubric.filter((i) => matchesFilter(i, filter))}
                  onSelect={setSelectedId}
                  explainer={{
                    label: "About s286 — the record-keeping duty",
                    body: (
                      <div className="space-y-2">
                        <p>
                          <span className="font-medium text-foreground">
                            What it requires.
                          </span>{" "}
                          Under <span className="font-medium">s286(1)</span> of
                          the Corporations Act 2001 (Cth) a company must keep
                          written financial records that (a) correctly record
                          and explain its transactions, financial position and
                          performance, and (b) would enable true and fair
                          financial statements to be prepared and audited. Under{" "}
                          <span className="font-medium">s286(2)</span> those
                          records must be retained for{" "}
                          <span className="font-medium">7 years</span>.
                        </p>
                        <p>
                          <span className="font-medium text-foreground">
                            Why it matters.
                          </span>{" "}
                          A failure to keep or retain adequate financial records
                          engages the{" "}
                          <span className="font-medium">s588E(4)</span>{" "}
                          presumption that the company was insolvent throughout
                          the deficient period — which in turn supports
                          insolvent-trading (
                          <span className="font-medium">s588G</span>) and other
                          recovery actions. The rows below are the record
                          categories a trading company is expected to hold.
                        </p>
                      </div>
                    ),
                  }}
                />
                <ItemTable
                  title="Cross-Reference Gaps"
                  tooltip="Case-specific records implied by what the other agents found — e.g. a registered security interest with no underlying contract, a loan with no agreement, or employees with no payroll/STP records. These are documents you'd expect to exist given the company's affairs but which aren't in Morgan."
                  items={gaps.filter((i) => matchesFilter(i, filter))}
                  onSelect={setSelectedId}
                  showSubtext={false}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="feed" className="mt-4 flex-1">
            {taskDetail ? (
              <UnifiedFeed
                task={taskDetail}
                subAgents={[]}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                taskTree={(taskDetail.workingMemory as any)?.taskTree}
                isRunning={running}
              />
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                The agent hasn't run yet. Run an assessment to see its activity
                here.
              </p>
            )}
          </TabsContent>
          <AiDisclaimer className="mt-4 text-center" />
        </Tabs>

        <BooksAndRecordsItemPanel
          item={selected}
          open={selected !== null}
          onClose={() => setSelectedId(null)}
          onViewDocument={(id) => setDocId(id as Id<"documents">)}
          isViewer={isViewer}
        />

        <DocumentDetail
          documentId={docId}
          isViewer={isViewer ?? false}
          open={docId !== null}
          onClose={() => setDocId(null)}
        />
      </div>
    </TooltipProvider>
  );
}

function FilterBar({
  counts,
  total,
  active,
  onChange,
}: {
  counts: {
    present: number;
    heldOffline: number;
    outstanding: number;
    notApplicable: number;
    criticalGaps: number;
  };
  total: number;
  active: FilterKey | null;
  onChange: (next: FilterKey | null) => void;
}) {
  const items: Array<{
    key: FilterKey | "all";
    label: string;
    icon: LucideIcon;
    iconColor?: string;
    count: number;
  }> = [
    { key: "all", label: "All", icon: LayoutGrid, count: total },
    {
      key: "present",
      label: STATE_META.present.label,
      icon: STATE_META.present.icon,
      iconColor: STATE_META.present.icon_color,
      count: counts.present,
    },
    {
      key: "held_offline",
      label: STATE_META.held_offline.label,
      icon: STATE_META.held_offline.icon,
      iconColor: STATE_META.held_offline.icon_color,
      count: counts.heldOffline,
    },
    {
      key: "outstanding",
      label: STATE_META.outstanding.label,
      icon: STATE_META.outstanding.icon,
      iconColor: STATE_META.outstanding.icon_color,
      count: counts.outstanding,
    },
    {
      key: "not_applicable",
      label: "N/A",
      icon: STATE_META.not_applicable.icon,
      iconColor: STATE_META.not_applicable.icon_color,
      count: counts.notApplicable,
    },
    {
      key: "critical",
      label: "Critical Gaps",
      icon: TriangleAlert,
      iconColor: counts.criticalGaps > 0 ? "text-red-600" : undefined,
      count: counts.criticalGaps,
    },
  ];

  return (
    <div className="flex gap-1 overflow-x-auto rounded-lg bg-secondary/40 p-1">
      {items.map((it) => {
        const isActive = it.key === "all" ? active === null : active === it.key;
        const Icon = it.icon;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() =>
              onChange(it.key === "all" ? null : (it.key as FilterKey))
            }
            className={cn(
              "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
              isActive
                ? "bg-background text-primary shadow-sm"
                : "text-primary/50 hover:text-primary/70",
            )}
          >
            <Icon
              className={cn("size-3.5", isActive ? it.iconColor : undefined)}
            />
            {it.label}
            <span
              className={cn(
                "rounded px-1.5 py-px text-[10px] tabular-nums transition",
                isActive
                  ? "bg-primary/10 text-primary/60"
                  : "bg-primary/5 text-primary/40",
              )}
            >
              {it.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function NotApplicableNotice({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 py-4">
        <Ban className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {children}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function HeadingWithTooltip({
  title,
  tooltip,
}: {
  title: string;
  tooltip: ReactNode;
}) {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <h2 className="text-sm font-semibold">{title}</h2>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground/60 hover:text-muted-foreground"
            aria-label={`About ${title}`}
          >
            <Info className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="max-w-[360px] text-xs leading-relaxed"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function ItemTable({
  title,
  tooltip,
  items,
  onSelect,
  showSubtext = true,
  explainer,
}: {
  title: string;
  tooltip: ReactNode;
  items: BooksRecordsItem[];
  onSelect: (id: string) => void;
  showSubtext?: boolean;
  explainer?: { label: string; body: ReactNode };
}) {
  const [explainerOpen, setExplainerOpen] = useState(false);
  if (items.length === 0) return null;
  return (
    <div>
      <HeadingWithTooltip title={title} tooltip={tooltip} />
      {explainer && (
        <div className="mb-2 rounded-md border bg-muted/30">
          <button
            type="button"
            onClick={() => setExplainerOpen((o) => !o)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
          >
            <Scale className="size-3.5 text-muted-foreground" />
            {explainer.label}
            <ChevronDown
              className={cn(
                "ml-auto size-4 text-muted-foreground transition-transform",
                explainerOpen && "rotate-180",
              )}
            />
          </button>
          {explainerOpen && (
            <div className="border-t px-3 py-2.5 text-sm leading-relaxed text-muted-foreground">
              {explainer.body}
            </div>
          )}
        </div>
      )}
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2">Record</th>
              <th className="w-32 px-2 py-2">Status</th>
              <th className="w-28 px-2 py-2">Adequacy</th>
              <th className="w-24 px-2 py-2">Severity</th>
              <th className="w-8 py-2 pr-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item._id}
                onClick={() => onSelect(item._id)}
                className="cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/40"
              >
                <td className="px-4 py-2.5">
                  <div className="font-medium">{item.title}</div>
                  {showSubtext &&
                    item.gapRationale &&
                    item.state === "outstanding" && (
                      <div className="max-w-[36rem] truncate text-xs text-muted-foreground">
                        {item.gapRationale}
                      </div>
                    )}
                </td>
                <td className="px-2 py-2.5">
                  <StateChip state={item.state} />
                </td>
                <td className="px-2 py-2.5">
                  <AdequacyChip status={item.adequacyStatus} />
                </td>
                <td className="px-2 py-2.5">
                  <SeverityChip severity={item.severity} />
                </td>
                <td className="py-2.5 pr-3 text-right">
                  <ChevronRight className="inline size-4 text-muted-foreground" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
